import { combineReducers } from 'redux';

import { PublicationCreatorAction, PublicationCreatorActionKeys } from './actions';
import { FileError } from '../ImportManager/actions';
import { Api } from '../../typings/api';

export enum PublicationSource {
	TEMPLATE = 'TEMPLATE',
	PUBLICATION = 'PUBLICATION',
	FILE = 'FILE',
}

export enum PublicationCreationStatus {
	IMPORTING = 'importing',
	PROCESSING = 'processing',
}

export enum LoadingStatus {
	NOT_LOADED = 'NOT_LOADED',
	LOADING = 'LOADING',
	LOADED = 'LOADED',
}

export interface PublicationCreationProgress {
	status?: PublicationCreationStatus | null;
	percentage?: number;
	error?: FileError | null;
}

export interface SelectedTemplate {
	id: number;
	label: string;
	format?: Api.PublicationFormat;
	isCustomTemplate?: boolean;
}

export interface PublicationCreatorState {
	source: PublicationSource | null;
	isDisplayed: boolean;
	requestId: string | null;
	fromTemplate: {
		masterTemplatesIds: number[];
		customTemplatesIds: number[];
		loadingStatus: LoadingStatus;
		selectedTemplate: SelectedTemplate | null;
		name: string;
		error: string | null;
	};
	fromFile: PublicationCreationProgress;
}

const creationProgressInitialState: PublicationCreationProgress = {
	status: null,
	percentage: 0,
	error: null,
};

const source = (state: PublicationSource | null = null, action: PublicationCreatorAction): PublicationSource | null => {
	switch (action.type) {
		case PublicationCreatorActionKeys.SHOW:
			return action.source;
		case PublicationCreatorActionKeys.HIDE:
			return null;
		default:
			return state;
	}
};

const masterTemplatesIds = (state: number[] = [], action: PublicationCreatorAction): number[] => {
	switch (action.type) {
		case PublicationCreatorActionKeys.SET_PUBLICATION_TEMPLATES:
			return action.masterTemplates.map(({ id }) => id);
		case PublicationCreatorActionKeys.HIDE:
			return [];
		default:
			return state;
	}
};

const customTemplatesIds = (state: number[] = [], action: PublicationCreatorAction): number[] => {
	switch (action.type) {
		case PublicationCreatorActionKeys.SET_PUBLICATION_TEMPLATES:
			return action.customTemplates.map(({ id }) => id);
		case PublicationCreatorActionKeys.REMOVE_TEMPLATE_FROM_STORE:
			return state.filter(id => id !== action.id);
		case PublicationCreatorActionKeys.HIDE:
			return [];
		default:
			return state;
	}
};

const loadingStatus = (state: LoadingStatus = LoadingStatus.NOT_LOADED, action: PublicationCreatorAction): LoadingStatus => {
	switch (action.type) {
		case PublicationCreatorActionKeys.GET_PUBLICATION_TEMPLATES:
			return LoadingStatus.LOADING;
		case PublicationCreatorActionKeys.SET_PUBLICATION_TEMPLATES:
			return LoadingStatus.LOADED;
		case PublicationCreatorActionKeys.HIDE:
			return LoadingStatus.NOT_LOADED;
		default:
			return state;
	}
};

const selectedTemplate = (state: SelectedTemplate | null = null, action: PublicationCreatorAction): SelectedTemplate | null => {
	switch (action.type) {
		case PublicationCreatorActionKeys.SELECT_TEMPLATE:
			return action.data;
		case PublicationCreatorActionKeys.HIDE:
			return null;
		default:
			return state;
	}
};

const isDisplayed = (state: boolean = false, action: PublicationCreatorAction): boolean => {
	switch (action.type) {
		case PublicationCreatorActionKeys.SHOW:
			return true;
		case PublicationCreatorActionKeys.HIDE:
			return false;
		default:
			return state;
	}
};

const requestId = (state: string | null = null, action: PublicationCreatorAction): string | null => {
	switch (action.type) {
		case PublicationCreatorActionKeys.SET_REQUEST_ID:
			return action.requestId;
		case PublicationCreatorActionKeys.REMOVE_FROM_CREATION_PROGRESS:
		case PublicationCreatorActionKeys.HIDE:
			return null;
		default:
			return state;
	}
};

const name = (state: string = '', action: PublicationCreatorAction): string => {
	switch (action.type) {
		case PublicationCreatorActionKeys.SET_NAME:
			return action.name;
		case PublicationCreatorActionKeys.SELECT_TEMPLATE:
			return `${action.data.label}-1`;
		case PublicationCreatorActionKeys.HIDE:
			return '';
		default:
			return state;
	}
};

const error = (state: string | null = null, action: PublicationCreatorAction): string | null => {
	switch (action.type) {
		case PublicationCreatorActionKeys.SET_ERROR:
			return action.error;
		case PublicationCreatorActionKeys.SELECT_TEMPLATE:
		case PublicationCreatorActionKeys.SET_NAME:
		case PublicationCreatorActionKeys.HIDE:
			return null;
		default:
			return state;
	}
};

const creationProgress = (state: PublicationCreationProgress = creationProgressInitialState, action: PublicationCreatorAction): PublicationCreationProgress => {
	switch (action.type) {
		case PublicationCreatorActionKeys.SET_CREATION_STATUS:
			return {
				...state,
				status: action.status,
				percentage: 0,
			};
		case PublicationCreatorActionKeys.UPDATE_CREATION_PROGRESS:
			if (!state.status) {
				return state;
			}
			return {
				...state,
				...action.data,
			};
		case PublicationCreatorActionKeys.REMOVE_FROM_CREATION_PROGRESS:
		case PublicationCreatorActionKeys.HIDE:
			return creationProgressInitialState;
		default:
			return state;
	}
};

export const publicationCreator = combineReducers<PublicationCreatorState>({
	source,
	isDisplayed,
	requestId,
	fromTemplate: combineReducers({
		masterTemplatesIds,
		customTemplatesIds,
		loadingStatus,
		selectedTemplate,
		name,
		error,
	}),
	fromFile: creationProgress,
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/PublicationCreator/reducer.ts